import { motion } from 'motion/react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Image, RhLink } from "@components";
import { AnimMode } from "@/utils/animUtils";
import { useMouse } from '@/providers/MouseContext';
import { ListInducteesFragment } from '@graphqlTypes';

import gsap from 'gsap';
import SplitText from 'gsap/SplitText';

import styles from './ListInductees.module.scss';
import className from 'classnames/bind';
import { getLinkData } from '@/core/utils';
const cx = className.bind(styles);

gsap.registerPlugin(SplitText);

type InducteesListInductee = {
  inductee: ListInductees[number] | ListInducteesFragment['nominees'][number];
  index: number;
  isRevealEnabled: boolean;
  isInView: boolean;
};

export default function InducteesListInductee({ inductee, index, isRevealEnabled, isInView }: InducteesListInductee) {
  const mouse = useMouse();
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [imageVisible, setImageVisible] = useState(false);

  const rootEl = useRef<HTMLDivElement>(null);
  const titleEl = useRef<HTMLParagraphElement>(null);

  const inducteeInfo = useMemo(() => {
    if (inductee.__typename === 'Inductee') {
      return {
        name: inductee.title,
        url: inductee.uri,
        image: inductee.pageData.featuredImage,
      }
    } else {
      const linkData = getLinkData(inductee.link, true);
      return {
        name: inductee.name,
        url: linkData?.url,
        target: linkData?.target,
        image: inductee.image,
      }
    }
  }, [inductee]);

  // -------------------- Mouse hover

  useEffect(() => {
    if (!rootEl.current) return;
    const rect = rootEl.current.getBoundingClientRect();
    const isHover =
      mouse.x >= rect.left &&
      mouse.x <= rect.right &&
      mouse.y >= rect.top &&
      mouse.y <= rect.bottom;
    setOffset({
      x: rect.left,
      y: rect.top,
    });
    setImageVisible(isHover);
  }, [mouse]);

  // -------------------- Animate in

  isRevealEnabled = AnimMode === 'all';

  const delay = 0.15 + ((index + 1) * 0.075);

  const charDelay = delay + 0.2;
  const charOpacity = 0;
  const charDistance = '120%';
  const charDuration = 0.8;
  const charEase = 'power3.out';

  // const charStagger = Math.floor(index / 3) % 2 === 1 ? -0.014 : 0.014; // Confusing as heck -Gabe

  const GROUP_SIZE = 3;
  const STAGGER_AMOUNT = 0.014;
  const group = Math.floor(index / GROUP_SIZE);
  const charStagger = (group % 2 === 1) ? -STAGGER_AMOUNT : STAGGER_AMOUNT;

  useEffect(() => {
    if (!isRevealEnabled || !isInView || !titleEl.current) return;

    // Split text into characters
    const split = new SplitText(titleEl.current, {
      type: 'chars',
      mask: 'chars',
      charsClass: cx('inductee-char'),
    });

    // GSAP timeline
    const tl = gsap.timeline({
      onComplete: () => {
        // split.revert(); // TODO: Not working right away, character positions shift. Revisit later. -Gabe
      }
    }).from(split.chars, {
      delay: charDelay,
      opacity: charOpacity,
      y: charDistance,
      duration: charDuration,
      ease: charEase,
      stagger: charStagger,
    });

    // Cleanup
    return () => {
      split.revert();
      tl.kill();
    }
  }, [isRevealEnabled, isInView]);

  // -------------------- Render

  return (
    <motion.div
      ref={rootEl}
      initial={isRevealEnabled ? { opacity: 0 } : {}}
      animate={isInView ? { opacity: 1 } : {}}
      transition={{ delay: delay, duration: 0.1, ease: "easeOut" }}
    >
      <RhLink
        className={cx('inductee', { hovered: imageVisible }, { reversed: Math.floor(index / 3) % 2 === 1 })} key={index}
        href={inducteeInfo.url}
        target={inducteeInfo.target}
      >
        <p ref={titleEl} className={cx('inductee-text')}>{inducteeInfo.name}</p>
        <div className={cx('spacer')} />
        <motion.div
          className={cx('spacer-reveal')}
          initial={isRevealEnabled ? { width: '100%' } : {}}
          animate={isInView ? { width: '0%' } : {}}
          transition={{ delay: delay, duration: 0.8 }}
          style={{
            left: Math.floor(index / 3) % 2 === 1 ? 0 : 'auto',
            right: Math.floor(index / 3) % 2 === 1 ? 'auto' : 0,
          }}
        />
        <Image
          image={inducteeInfo.image}
          sizes="(max-width: 768px) 50vw, 524px"
          className={cx('inductee-image')}
          style={{
            top: `${mouse.y - offset.y}px`,
          }}
        />
      </RhLink>
    </motion.div>
  );
}